import React, { useMemo, useEffect, useState } from "react";
import { Menu, Box, HStack, VStack, Text, Pressable, ScrollView, Divider, useToast } from "native-base";
import { AiOutlineRight } from "react-icons/ai";
import { IoCheckmarkDoneSharp } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import AppSpinner from "atoms/AppSpinner";
import colors from "theme/colors";
import { useMarkNotificationsRead } from "api/useQueryCalls";
import { useNotifications } from "customHooks/useNotifications";
import { showErrorToast } from "utils/utilities";
import { getStore } from "store/storeUtils";
import NotificationBell from "molecules/NotificationBell";
import { NOTIFICATIONS } from "global/paths";
import { getNotificationNavigation } from "utils/notifications";
import { DROPDOWN_NOTIFICATION_LIMITS, truncateWithEllipsis } from "utils/notificationText";
import {
  NOTIF_ENTRY_POINT,
  logNotifCenterOpened,
  logNotificationClick,
} from "utils/analytics/analyticsNotificationDropdown";
import styles from "./styles";

export function shouldRenderNotifcationsContent(hasItems, sections, getNotificationText, t, onNotificationPress) {
  return hasItems ? (
    <VStack space={4}>
      {sections.map((section) => (
        <VStack key={section.id} space={3}>
          <Text style={styles.sectionLabel}>{section.label}</Text>
          <VStack space={2}>
            {section.items.map((item) => {
              const { title, description, truncatedTitle, truncatedDescription } = getNotificationText(item);
              const createdAt = item.createdAt ? dayjs(item.createdAt) : null;
              const timeText = createdAt && createdAt.isValid() ? createdAt.format("HH:mm") : item.time;

              return (
                <Pressable
                  key={item.id}
                  _hover={styles.itemHover}
                  borderRadius="8px"
                  onPress={() => onNotificationPress(item)}
                >
                  <HStack space={3} alignItems="flex-start" style={styles.itemContainer}>
                    {!item.isRead ? <Box style={styles.dot} /> : <Box style={styles.dotHidden} />}
                    <VStack space={1} flex={1}>
                      <Text style={styles.itemTitle} title={truncatedTitle !== title ? title : undefined}>
                        {truncatedTitle}
                      </Text>
                      <Text
                        fontSize="12px"
                        style={styles.itemDescription}
                        title={truncatedDescription !== description ? description : undefined}
                      >
                        {truncatedDescription}
                      </Text>
                      <Text style={styles.timeText}>{timeText}</Text>
                    </VStack>
                  </HStack>
                </Pressable>
              );
            })}
          </VStack>
        </VStack>
      ))}
    </VStack>
  ) : (
    <VStack py={6} alignItems="center" space={2}>
      <Text fontWeight="600" color={colors.neutral.charcoal}>
        {t("notifications.emptyTitle")}
      </Text>
      <Text style={styles.sectionLabel}>{t("notifications.emptySubtitle")}</Text>
    </VStack>
  );
}

function shouldRenderNotificationsFooter(hasItems, isMarkAsReadDisabled, handleMarkAsRead, handleSeeAll, t) {
  return hasItems ? (
    <>
      <Divider bg={colors.disabled.smoke} />
      <HStack justifyContent="space-between" alignItems="center">
        <Pressable onPress={handleMarkAsRead} disabled={isMarkAsReadDisabled} isDisabled={isMarkAsReadDisabled}>
          <HStack alignItems="center" space={2}>
            <IoCheckmarkDoneSharp
              size="16px"
              color={isMarkAsReadDisabled ? colors.disabled.smoke : colors.secondary.orchid}
            />
            <Text style={isMarkAsReadDisabled ? styles.footerButtonDisabled : styles.footerButton}>
              {t("notifications.markAsRead")}
            </Text>
          </HStack>
        </Pressable>
        <Pressable onPress={handleSeeAll}>
          <HStack alignItems="center" space={1}>
            <Text style={styles.footerButton}>{t("notifications.seeAll")}</Text>
            <AiOutlineRight size="14px" color={colors.secondary.orchid} />
          </HStack>
        </Pressable>
      </HStack>
    </>
  ) : null;
}

const NotificationMenu = () => {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const toast = useToast();
  const { resetLoginData } = getStore();
  const [isMenuOpen, setMenuOpen] = useState(false);
  const {
    mutate: markNotificationsRead,
    isPending: isMarkReadPending,
    isError: isMarkReadError,
    error: markReadError,
  } = useMarkNotificationsRead();

  const {
    notifications: items,
    hasUnreadNotifications,
    isLoading: isLoadingNotifications,
  } = useNotifications({ status: "all", limit: 5 });

  useEffect(() => {
    if (isMarkReadError) {
      showErrorToast({ error: markReadError, toast, navigate, resetLoginData });
    }
  }, [isMarkReadError, markReadError]);

  const sections = useMemo(() => {
    const grouped = [];
    const indexByKey = new Map();

    items.forEach((item) => {
      const createdAt = item.createdAt ? dayjs(item.createdAt) : null;
      const hasValidCreatedAt = createdAt && createdAt.isValid();
      const isToday = hasValidCreatedAt ? createdAt.isSame(dayjs(), "day") : item.sectionKey === "today";
      const label = hasValidCreatedAt
        ? isToday
          ? t("notifications.today")
          : createdAt.format("D MMMM YYYY")
        : item.sectionKey === "today"
        ? t("notifications.today")
        : item.sectionLabel;
      const key = hasValidCreatedAt
        ? createdAt.format("YYYY-MM-DD")
        : item.sectionKey || item.sectionLabel || "unknown";
      if (!indexByKey.has(key)) {
        indexByKey.set(key, grouped.length);
        grouped.push({ id: key, label, items: [] });
      }
      grouped[indexByKey.get(key)].items.push(item);
    });

    return grouped;
  }, [items, t]);

  const getNotificationText = (item) => {
    const interpolate = (text, meta = {}) =>
      text.replace(/\{(\w+)\}/g, (match, key) => (meta[key] !== undefined ? meta[key] : match));

    const isIndonesian = i18n.language?.toLowerCase().startsWith("id");
    const titleFromPayload = isIndonesian ? item.title_id : item.title_en;
    const bodyFromPayload = isIndonesian ? item.body_id : item.body_en;

    const title =
      titleFromPayload || interpolate(t(`notifications.notificationsType.${item.type}.title`, item.meta), item.meta);
    const description =
      bodyFromPayload || interpolate(t(`notifications.notificationsType.${item.type}.body`, item.meta), item.meta);

    return {
      title,
      description,
      truncatedTitle: truncateWithEllipsis(title, DROPDOWN_NOTIFICATION_LIMITS.title),
      truncatedDescription: truncateWithEllipsis(description, DROPDOWN_NOTIFICATION_LIMITS.body),
    };
  };

  const getNotificationIds = (notificationItems) => notificationItems.map((item) => item.id);

  const hasItems = items.length > 0;
  const hasUnreadVisibleItems = items.some((item) => !item.isRead);
  const isInitialLoading = isLoadingNotifications && !hasItems;
  const isMarkAsReadDisabled = !hasUnreadVisibleItems || isMarkReadPending;

  const handleMarkAsRead = () => {
    const ids = getNotificationIds(items.filter((item) => !item.isRead));
    if (ids.length === 0) return;
    markNotificationsRead({ notificationIds: ids });
  };

  const handleSeeAll = () => {
    setMenuOpen(false);
    navigate(NOTIFICATIONS);
  };

  const handleNotificationPress = (item) => {
    if (item?.id && !item?.isRead) {
      markNotificationsRead({ notificationIds: [item.id] });
    }

    if (item?.isActionable !== true) return;

    const navigation = getNotificationNavigation(item);
    if (!navigation?.target) return;

    logNotificationClick({
      entryPoint: NOTIF_ENTRY_POINT.DROPDOWN,
      notificationType: item?.type,
      statusBefore: item?.isRead ? "read" : "unread",
    });

    setMenuOpen(false);
    navigate(
      {
        pathname: navigation.target,
        search: navigation.search || "",
      },
      { state: navigation.state },
    );
  };
  return (
    <Menu
      isOpen={isMenuOpen}
      onOpen={() => {
        setMenuOpen(true);
        logNotifCenterOpened();
      }}
      onClose={() => setMenuOpen(false)}
      closeOnSelect={false}
      shouldOverlapWithTrigger={false}
      placement="bottom right"
      trigger={(triggerProps) => <NotificationBell hasNotifications={hasUnreadNotifications} {...triggerProps} />}
      style={styles.menu}
    >
      <VStack space={3}>
        <Text style={styles.header}>{t("notifications.title")}</Text>
        <Divider bg={colors.disabled.smoke} />
        <ScrollView maxH="420px">
          {isInitialLoading ? (
            <AppSpinner height="180px" size="sm" />
          ) : (
            shouldRenderNotifcationsContent(hasItems, sections, getNotificationText, t, handleNotificationPress)
          )}
        </ScrollView>
        {shouldRenderNotificationsFooter(hasItems, isMarkAsReadDisabled, handleMarkAsRead, handleSeeAll, t)}
      </VStack>
    </Menu>
  );
};

export default NotificationMenu;
